import React from 'react';
import PropTypes from 'prop-types';
import config from '../config';
import {cutString} from '../util';
import {attachmentsIcons as attachmentsIconsViews} from '../assets/svgIcons';

import '../styles/Node.css';
import svgElementsStyles from '../styles/GraphSVGElements';

import EdgeButton from './EdgeButton';

const labelInnerBoxWidth = config.sizes.nodeWidth - config.sizes.nodeInnerPadding * 2;
const labelInnerBoxHeight = config.sizes.edgeLabelInnerBoxHeight;

class Node extends React.Component {
    shouldComponentUpdate(nextProps, nextState) {
        const shallowCompareKeys = ['x', 'y', 'name', 'message', 'height', 'terminalMessage', 'isHighlighted'];
        for (const key of shallowCompareKeys) {
            if (nextProps[key] !== this.props[key]) {
                return true;
            }
        }

        const {highlightedEdgesIds, foldedEdgeIds} = this.props;
        const nextHighlightedEdgesIds = nextProps.highlightedEdgesIds;
        const nextFoldedEdgeIds = nextProps.foldedEdgeIds;

        if (nextHighlightedEdgesIds.length !== highlightedEdgesIds.length || nextFoldedEdgeIds.length !== foldedEdgeIds.length) {
            return true;
        }

        for (const id of nextHighlightedEdgesIds) {
            if (!highlightedEdgesIds.includes(id)) {
                return true;
            }
        }

        const nextEdges = nextProps.edges;
        const {edges} = this.props;
        if (nextEdges.length !== edges.length) {
            return true;
        }

        for (let i = 0; i < nextEdges.length; ++i) {
            if (nextEdges[i].isNotEqual(edges[i])) {
                return true;
            }
        }


        return false;
    }

    render() {
        const {
            x,
            y,
            name,
            message,
            edges,
            isHighlighted,
            terminalMessage,
            height,
            highlightedEdgesIds,
            foldedEdgeIds,
            chatWithOperator
        } = this.props;
        const transform = `translate( ${x}, ${y})`;
        const transformNodeText = `translate( ${config.sizes.nodeInnerPadding}, 50)`;
        const transformEdgesLabelGroup = `translate(${config.sizes.nodeInnerPadding}, ${config.sizes.nodeHeaderHeight})`;
        const transformEdgeEndLabelY = config.sizes.nodeHeaderHeight + edges.length * config.sizes.edgeLabelBoxHeight;
        const transformEndEdgeLabel = `translate(${config.sizes.nodeInnerPadding},${transformEdgeEndLabelY})`;
        const transformChatOperatorLabelY = terminalMessage ? transformEdgeEndLabelY + 50 : transformEdgeEndLabelY;
        const transformChatOperatorLabel = `translate(${config.sizes.nodeInnerPadding},${transformChatOperatorLabelY})`;

        const edgeLabels = edges.map(edge => (
            <EdgeButton
                key={edge.id}
                id={edge.id}
                isConnected={edge.destination !== null}
                isFolded={foldedEdgeIds.includes(edge.id)}
                y={edge.y}
                text={edge.text}
                isHighlighted={highlightedEdgesIds.includes(edge.id)}
            />
        ));

        const stroke = isHighlighted ? '#2A60E4' : '#8b8b91';
        const strokeWidth = isHighlighted ? config.sizes.highlightStrokeWidth : 1;

        return (
            <g transform={transform} className="node">
                <rect {...svgElementsStyles.nodeCard} stroke={stroke} strokeWidth={strokeWidth} height={height}
                      rx={10}
                      ry={10}
                      width={config.sizes.nodeWidth}/>
                <text className="svg-text" {...svgElementsStyles.nodeNameText}>
                    {cutString(name, 17)}
                </text>
                <g transform={transformNodeText}>
                    <rect width={labelInnerBoxWidth} height={labelInnerBoxHeight}
                          className="cell" {...svgElementsStyles.nodeMessageTextCell}
                          rx={4}
                          ry={4}/>
                    <text className="svg-text" {...svgElementsStyles.nodeMessageText}>
                        {cutString(message, 17)}
                    </text>
                </g>
                <g transform={transformEdgesLabelGroup}>
                    {edgeLabels}
                </g>
                {terminalMessage
                    && (
                        <g transform={transformEndEdgeLabel}>
                            <rect x="0px" className="cell" {...svgElementsStyles.endCell} width={labelInnerBoxWidth}
                                  rx={10}
                                  ry={10}
                                  fill={'rgba(255,255,255,0.99)'}
                                  height={labelInnerBoxHeight}/>
                            <text x="15px" y="25px" className="text svg-text">
                                End Conversation
                            </text>
                        </g>
                    )}
                {chatWithOperator && (
                    <g transform={transformChatOperatorLabel}>
                        <rect x="0px" className="cell" {...svgElementsStyles.endCell} width={labelInnerBoxWidth}
                              rx={10}
                              ry={10}
                              fill={'rgba(255,255,255,0.99)'}
                              height={labelInnerBoxHeight}/>
                        <text x="15px" y="25px" className="text svg-text">
                            Chat with Operator
                        </text>
                    </g>
                )}
                {this.getAttachmentBoxView()}
            </g>
        );
    }

    getAttachmentBoxView() {
        const {edges, terminalMessage, chatWithOperator} = this.props;
        const transformAttachmentBoxY = config.sizes.nodeHeaderHeight
            + edges.length * config.sizes.edgeLabelBoxHeight
            + terminalMessage * config.sizes.edgeLabelBoxHeight
            + chatWithOperator * config.sizes.edgeLabelBoxHeight;
        const transformAttachmentBox = `translate(${config.sizes.nodeInnerPadding},${transformAttachmentBoxY})`;

        const icons = this.getAttachmentIcons();
        if (!icons.length) {
            return (<g/>);
        }

        return (
            <g transform={transformAttachmentBox}>
                {icons}
            </g>
        );
    }

    getAttachmentIcons() {
        const {includedTypesInAttachments} = this.props;
        const attachmentsIcons = [];
        for (let i = 0; i < includedTypesInAttachments.length; ++i) {
            const translateX = i * (config.sizes.attachmentIconSize + config.sizes.attachmentIconSpacing);
            const icon = (
                <g
                    className="file-icon"
                    key={i}
                    transform={`translate(${translateX},0)`}
                >
                    {attachmentsIconsViews[includedTypesInAttachments[i]]}
                </g>
            );
            attachmentsIcons.push(icon);
        }

        return attachmentsIcons;
    }
}

Node.propTypes = {
    name: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    terminalMessage: PropTypes.bool.isRequired,
    includedTypesInAttachments: PropTypes.array.isRequired,
    isHighlighted: PropTypes.bool.isRequired,
    edges: PropTypes.array.isRequired,
    highlightedEdgesIds: PropTypes.array.isRequired,
    foldedEdgeIds: PropTypes.array.isRequired,
    chatWithOperator: PropTypes.bool.isRequired
};

export default Node;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/graph/components/Node.jsx